import React, { useMemo, useState, useEffect } from "react";
import { Global, css } from "@emotion/react";
import styled from "@emotion/styled";
import moment from "moment";
import Slider from "rc-slider";
import ParkkiTheme from "themes/ParkkiTheme";

import "rc-slider/assets/index.css";
import "rc-tooltip/assets/bootstrap.css";

const DOT_EACH_X_MINUTES = 1;
const RANGE_IN_HOUR = 24;
// @ts-ignore
const SliderWithTooltip = Slider.createSliderWithTooltip(Slider);

const styles = {
  trackStyle: {
    backgroundColor: ParkkiTheme.colors.turquoise,
    height: "14px",
  },
  railStyle: {
    backgroundColor: "#F0F0F0",
    height: "14px",
  },
  handleStyle: {
    backgroundColor: ParkkiTheme.colors.blue,
    borderColor: "#B2B0B0",
    borderWeight: "7px",
    height: "18px",
    width: "18px",
    bottom: "-7px",
  },
  dotStyle: {
    backgroundColor: "rgba(0,0,0,0)",
    borderColor: "rgba(0,0,0,0)",
  },
  activeDotStyle: {
    backgroundColor: "rgba(0,0,0,0)",
    borderColor: "rgba(0,0,0,0)",
  },
  sliderMarks: {
    backgroundColor: "white",
    marginLeft: "-5%",
    marginTop: "5px",
    width: "40px",
    paddingTop: "2px",
    paddingBottom: "2px",
    color: "#666",
  },
};

const Container = styled.div`
  width: 100%;
`;

type Props = {
  disabled: boolean;
  selectedTimestamp: number;
  onHoursSelected: Function;
};

function HoursSlider({
  disabled,
  onHoursSelected,
  selectedTimestamp,
}: Props): JSX.Element {
  const maxValue = useMemo(() => {
    return RANGE_IN_HOUR * (60 / DOT_EACH_X_MINUTES) - 1;
  }, []);

  const currentValue = useMemo(() => {
    let time = moment(selectedTimestamp * 1000);

    if (time.isAfter(new Date(), "hours")) {
      const date = new Date();
      const now = Math.round(date.getTime() / 1000);
      onHoursSelected(now);
      time = moment(date);
    }

    return (time.hours() * 60 + time.minutes()) / DOT_EACH_X_MINUTES;
  }, [selectedTimestamp, onHoursSelected]);
  const [sliderValue, setSliderValue] = useState(currentValue);

  useEffect(() => {
    setSliderValue(currentValue);
  }, [currentValue]);

  function getMomentFromTimestamp(timestamp: number) {
    const hours = (timestamp / 60) | 0;
    const minutes = timestamp % 60 | 0;

    return moment(selectedTimestamp * 1000)
      .hours(hours)
      .minutes(minutes);
  }

  function formatTip(selectedNumber: number) {
    return getMomentFromTimestamp(selectedNumber * DOT_EACH_X_MINUTES).format(
      "LT"
    );
  }

  function handleAfterTimeChange(sliderValue: number) {
    const now = moment();
    let sliderMoment = getMomentFromTimestamp(sliderValue * DOT_EACH_X_MINUTES);

    if (now < sliderMoment) {
      const updatedSliderValue =
        (now.hours() * 60 + now.minutes()) / DOT_EACH_X_MINUTES;
      setSliderValue(updatedSliderValue);

      sliderMoment = now;
    }

    onHoursSelected(sliderMoment.unix());
  }

  return (
    <Container>
      <Global
        styles={css`
          .rc-slider-disabled {
            background-color: transparent !important;
          }
        `}
      />
      <SliderWithTooltip
        disabled={disabled}
        marks={{
          0: { label: "00:00", style: styles.sliderMarks },
          [maxValue]: {
            label: formatTip(maxValue),
            style: styles.sliderMarks,
          },
        }}
        tipProps={{ placement: "bottom", zIndex: 400 }}
        min={0}
        max={maxValue}
        defaultValue={currentValue}
        value={sliderValue}
        tipFormatter={formatTip}
        onAfterChange={handleAfterTimeChange}
        onChange={setSliderValue}
        railStyle={styles.railStyle}
        trackStyle={styles.trackStyle}
        dotStyle={styles.dotStyle}
        handleStyle={[styles.handleStyle]}
        activeDotStyle={styles.activeDotStyle}
      />
    </Container>
  );
}

export default HoursSlider;
